import React from 'react';
import QueryLoadingState from './QueryLoadingState';
import QueryErrorState from './QueryErrorState';
import QueryEmptyState from './QueryEmptyState';
import QueryBackgroundSync from './QueryBackgroundSync';
import QueryStaleIndicator from './QueryStaleIndicator';
import './QueryStateView.scss';

/**
 * QueryStateView Component
 * Unified declarative wrapper handling all 5 server-state lifecycle stages:
 * 1. Loading state (initial query fetch)
 * 2. Error state (with retry action & debug info)
 * 3. Empty state (when data array is empty or record not found)
 * 4. Background refetching state (live sync pulse indicator)
 * 5. Stale data state (staleness badge + manual refresh)
 *
 * @param {Object} props
 * @param {Object} [props.query] - Direct TanStack Query result object (useQuery hook return)
 * @param {boolean} [props.isLoading] - Manual override for loading
 * @param {boolean} [props.isError] - Manual override for error
 * @param {Error|Object} [props.error] - Manual error object
 * @param {any} [props.data] - Manual query data
 * @param {boolean} [props.isFetching] - Manual isFetching flag
 * @param {boolean} [props.isStale] - Manual isStale flag
 * @param {Function} [props.refetch] - Manual refetch trigger
 * @param {number} [props.dataUpdatedAt] - Timestamp of last fetch
 * @param {boolean|Function} [props.isEmpty] - Custom empty predicate or boolean
 * @param {'skeleton'|'cards'|'table'|'timeline'|'spinner'} [props.loadingVariant='skeleton']
 * @param {number} [props.loadingCount=3]
 * @param {string} [props.loadingMessage]
 * @param {React.ReactNode} [props.loadingFallback]
 * @param {React.ReactNode} [props.errorFallback]
 * @param {string} [props.errorTitle]
 * @param {React.ReactNode} [props.emptyFallback]
 * @param {string} [props.emptyTitle]
 * @param {string} [props.emptyDescription]
 * @param {React.ReactNode} [props.emptyIcon]
 * @param {string} [props.emptyActionLabel]
 * @param {Function} [props.onEmptyAction]
 * @param {boolean} [props.showBackgroundSync=true]
 * @param {boolean} [props.showStaleIndicator=false]
 * @param {string} [props.className='']
 * @param {React.ReactNode|Function} props.children
 */
export const QueryStateView = ({
  query,
  isLoading,
  isError,
  error,
  data,
  isFetching,
  isStale,
  refetch,
  dataUpdatedAt,
  isEmpty,
  loadingVariant = 'skeleton',
  loadingCount = 3,
  loadingMessage,
  loadingFallback,
  errorFallback,
  errorTitle,
  emptyFallback,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  emptyActionLabel,
  onEmptyAction,
  showBackgroundSync = true,
  showStaleIndicator = false,
  className = '',
  children,
}) => {
  // Extract values from query object if provided, allowing props overrides
  const resolvedIsLoading = isLoading ?? query?.isLoading ?? false;
  const resolvedIsError = isError ?? query?.isError ?? false;
  const resolvedError = error ?? query?.error ?? null;
  const resolvedData = data ?? query?.data;
  const resolvedIsFetching = isFetching ?? query?.isFetching ?? false;
  const resolvedIsStale = isStale ?? query?.isStale ?? false;
  const resolvedRefetch = refetch ?? query?.refetch;
  const resolvedDataUpdatedAt = dataUpdatedAt ?? query?.dataUpdatedAt;

  // 1. Initial Loading State
  if (resolvedIsLoading) {
    if (loadingFallback) return loadingFallback;
    return (
      <QueryLoadingState
        variant={loadingVariant}
        count={loadingCount}
        message={loadingMessage}
        className={className}
      />
    );
  }

  // 2. Error State
  if (resolvedIsError) {
    if (errorFallback) return errorFallback;
    return (
      <QueryErrorState
        error={resolvedError}
        title={errorTitle}
        onRetry={resolvedRefetch}
        isRetrying={resolvedIsFetching}
        className={className}
      />
    );
  }

  // 3. Empty State Calculation
  const checkIsEmpty = () => {
    if (typeof isEmpty === 'function') return isEmpty(resolvedData);
    if (typeof isEmpty === 'boolean') return isEmpty;
    if (resolvedData === undefined || resolvedData === null) return true;
    if (Array.isArray(resolvedData) && resolvedData.length === 0) return true;
    if (typeof resolvedData === 'object' && Object.keys(resolvedData).length === 0) return true;
    return false;
  };

  const isDataEmpty = checkIsEmpty();

  if (isDataEmpty) {
    if (emptyFallback) return emptyFallback;
    return (
      <div className={`query-state-view-container ${className}`}>
        {showBackgroundSync && (
          <QueryBackgroundSync isFetching={resolvedIsFetching} isLoading={resolvedIsLoading} />
        )}
        <QueryEmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={emptyIcon}
          actionLabel={emptyActionLabel}
          onAction={onEmptyAction}
        />
      </div>
    );
  }

  // 4 & 5. Success Content (with Background Sync & Stale Status)
  return (
    <div className={`query-state-view-container ${className}`}>
      {(showBackgroundSync || showStaleIndicator) && (
        <div className="query-state-meta-bar">
          {showBackgroundSync && (
            <QueryBackgroundSync isFetching={resolvedIsFetching} isLoading={resolvedIsLoading} />
          )}
          {showStaleIndicator && (
            <QueryStaleIndicator
              isStale={resolvedIsStale}
              dataUpdatedAt={resolvedDataUpdatedAt}
              onRefresh={resolvedRefetch}
              isFetching={resolvedIsFetching}
            />
          )}
        </div>
      )}

      {typeof children === 'function'
        ? children({
            data: resolvedData,
            isFetching: resolvedIsFetching,
            isStale: resolvedIsStale,
            refetch: resolvedRefetch,
          })
        : children}
    </div>
  );
};

export default QueryStateView;
